import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useScope } from '../../hooks/useScope';
import { useTranslation } from '../../hooks/useTranslation';
import { generateCompleteCreditPlan } from '../../services/creditPlanService';
import { submitAssessmentForm } from '../../services/assessmentService';
import { getParsedCreditReports, getParsedCreditReportById, getEffectiveCreditSummary } from '../../services/creditReportService';
import { trackFeatureUsed, trackCTAClick } from '../../services/analyticsService';
import MarkdownRenderer from './MarkdownRenderer';
import { useRateLimitedFunction } from '../../hooks/useRateLimiter';
import DisclaimerModal from './DisclaimerModal';
import Modal from '../common/Modal';
import './ReportBasedAssessmentForm.css';

/**
 * Normalize creditor name for display and deduplication
 * Maps common bank name variations to canonical display names
 */
const normalizeCreditorName = (name) => {
  if (!name) return '';

  let normalized = name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Remove common suffixes
  const suffixes = ['bank', 'na', 'n a', 'usa', 'cbna', 'card services', 'card serv', 'cards', 'card', 'plcc', 'credit', 'services', 'serv', 'national association'];
  for (const suffix of suffixes) {
    const suffixRegex = new RegExp(`\\s*${suffix}\\s*$`, 'i');
    normalized = normalized.replace(suffixRegex, '');
  }

  // Map to canonical display names
  const bankMappings = {
    'jpmcb': 'Chase',
    'jpmorgan chase': 'Chase',
    'syncb amazon': 'Amazon',
    'syncb': 'Synchrony',
    'citi': 'Citi',
    'citicards': 'Citi',
    'capital one': 'Capital One',
    'capitalone': 'Capital One',
    'bofa': 'Bank of America',
    'bank of america': 'Bank of America',
    'bankofamerica': 'Bank of America',
    'td': 'TD Bank',
    'tdbank': 'TD Bank',
    'wfbna': 'Wells Fargo',
    'wells fargo': 'Wells Fargo',
    'wellsfargo': 'Wells Fargo',
    'amex': 'American Express',
    'american express': 'American Express',
    'americanexpress': 'American Express',
    'discover': 'Discover',
    'usaa': 'USAA',
    'navy federal': 'Navy Federal',
    'navyfederal': 'Navy Federal',
  };

  // Check for known mappings
  const normalizedKey = normalized.replace(/\s+/g, '');
  for (const [variant, canonical] of Object.entries(bankMappings)) {
    const variantKey = variant.replace(/\s+/g, '');
    if (normalizedKey.startsWith(variantKey) || normalizedKey === variantKey) {
      return canonical;
    }
  }

  // Capitalize first letter of each word for display
  return normalized.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

/**
 * Check if an account is a credit card or revolving account
 */
const isCreditCardAccount = (account) => {
  const type = (account.accountType || '').toLowerCase();

  return type.includes('credit card') ||
    type.includes('creditcard') ||
    type.includes('charge') ||
    type.includes('revolving');
};

const ReportBasedAssessmentForm = ({ onClose, onSubmit }) => {
  const { currentUser } = useAuth();
  const { isManaged, isClientView, effectiveUserId, actualUserId } = useScope();
  const t = useTranslation();
  
  // Report data state
  const [reportData, setReportData] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportError, setReportError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    creditScore: '',
    monthlyIncome: '',
    improvementPriority: 'overall',
    timeline: '6-12 months'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [creditPlan, setCreditPlan] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Rate limit: 3 submissions per minute to prevent spam
  const { execute: submitWithRateLimit, getRemainingCalls } = useRateLimitedFunction(
    async (data) => await submitAssessmentForm(data),
    3,
    60000
  );

  // Determine scope parameters
  const isMentorMasquerade = isManaged && !isClientView;
  const clientUserId = isMentorMasquerade ? effectiveUserId : null;

  // Ref to prevent duplicate loading
  const hasLoadedRef = useRef(false);

  // Aggregate summaries from multiple bureau reports - memoized to prevent recreation
  const aggregateReportSummaries = useCallback((reports) => {
    // Get effective summary for each report
    const summaries = reports.map(report => {
      // Try reportData first, then fall back to report itself
      const dataToProcess = report.reportData || report;
      return getEffectiveCreditSummary(dataToProcess);
    });

    // Aggregate values across all reports (use max to get most complete data)
    let totalAccounts = 0;
    let openAccounts = 0;
    let closedAccounts = 0;
    let delinquentAccounts = 0;
    let derogatoryAccounts = 0;
    let totalBalances = 0;
    let revolvingBalance = 0;
    let totalCreditLimit = 0;
    let creditUtilization = null;
    let hardInquiries = 0;
    let collections = 0;
    let oldestAccountDate = null;

    // Track credit cards with their opening dates (deduplicated by normalized name)
    const creditCardsMap = new Map();

    reports.forEach((report, idx) => {
      const summary = summaries[idx];

      // Aggregate summary metrics
      if (summary) {
        totalAccounts = Math.max(totalAccounts, summary.totalAccounts || 0);
        openAccounts = Math.max(openAccounts, summary.openAccounts || 0);
        closedAccounts = Math.max(closedAccounts, summary.closedAccounts || 0);
        delinquentAccounts = Math.max(delinquentAccounts, summary.delinquentAccounts || 0);
        derogatoryAccounts = Math.max(derogatoryAccounts, summary.derogatoryAccounts || 0);
        totalBalances = Math.max(totalBalances, summary.totalBalances || 0);
        revolvingBalance = Math.max(revolvingBalance, summary.revolvingBalance || 0);
        totalCreditLimit = Math.max(totalCreditLimit, summary.totalCreditLimit || 0);
        // Use the first non-null utilization value
        if (creditUtilization == null && summary.creditUtilization != null) {
          creditUtilization = summary.creditUtilization;
        }
      }

      // Count hard inquiries from report data
      const reportData = report.reportData || report;
      const inquiries = reportData.inquiries;
      if (inquiries) {
        // Handle both { hard: [...] } and direct array formats
        const hardInq = Array.isArray(inquiries.hard) ? inquiries.hard : (Array.isArray(inquiries) ? inquiries : []);
        hardInquiries = Math.max(hardInquiries, hardInq.length);
      }

      // Count collections from report data
      const reportCollections = reportData.collections;
      if (Array.isArray(reportCollections)) {
        collections = Math.max(collections, reportCollections.length);
      } else if (summary && summary.collections) {
        // Fallback to summary if available
        collections = Math.max(collections, summary.collections);
      }

      // Extract credit cards and track oldest account date from all accounts
      const accounts = reportData.accounts || [];
      accounts.forEach(account => {
        // Track oldest account date across all accounts
        if (account.dateOpened) {
          const openedDate = new Date(account.dateOpened);
          if (!isNaN(openedDate.getTime())) {
            if (oldestAccountDate === null || openedDate < oldestAccountDate) {
              oldestAccountDate = openedDate;
            }
          }
        }

        // Extract credit cards specifically
        if (isCreditCardAccount(account)) {
          const normalizedName = normalizeCreditorName(account.creditorName);
          const dateOpened = account.dateOpened || '';

          // Use normalized name as key for deduplication
          const key = normalizedName.toLowerCase().replace(/\s+/g, '');

          if (!creditCardsMap.has(key)) {
            creditCardsMap.set(key, {
              bankName: normalizedName,
              dateOpened: dateOpened,
              creditLimit: account.creditLimit,
              currentBalance: account.currentBalance,
              bureau: report.bureau
            });
          } else {
            // Merge: prefer non-empty values and use earliest date
            const existing = creditCardsMap.get(key);
            const existingDate = existing.dateOpened ? new Date(existing.dateOpened) : null;
            const newDate = dateOpened ? new Date(dateOpened) : null;

            creditCardsMap.set(key, {
              ...existing,
              dateOpened: (newDate && (!existingDate || newDate < existingDate)) ? dateOpened : existing.dateOpened,
              creditLimit: existing.creditLimit || account.creditLimit,
              currentBalance: existing.currentBalance ?? account.currentBalance
            });
          }
        }
      });
    });

    // Convert credit cards map to array and sort by date opened (oldest first)
    const creditCards = Array.from(creditCardsMap.values()).sort((a, b) => {
      if (!a.dateOpened && !b.dateOpened) return 0;
      if (!a.dateOpened) return 1;
      if (!b.dateOpened) return -1;
      return new Date(a.dateOpened) - new Date(b.dateOpened);
    });

    // Format oldest account date as string
    let oldestAccountDateStr = '';
    if (oldestAccountDate) {
      const year = oldestAccountDate.getFullYear();
      const month = String(oldestAccountDate.getMonth() + 1).padStart(2, '0');
      oldestAccountDateStr = `${year}-${month}`;
    }

    return {
      totalAccounts,
      openAccounts,
      closedAccounts,
      delinquentAccounts,
      derogatoryAccounts,
      totalBalances,
      revolvingBalance,
      totalCreditLimit,
      creditUtilization,
      hardInquiries,
      collections,
      oldestAccountDate: oldestAccountDateStr,
      creditCards,
      bureausAnalyzed: reports.map(r => r.bureau)
    };
  }, []);

  // Load parsed credit reports on mount
  useEffect(() => {
    // Prevent duplicate loading
    if (hasLoadedRef.current) {
      return;
    }
    
    const loadReports = async () => {
      hasLoadedRef.current = true;
      
      try {
        setLoadingReports(true);
        setReportError(null);
        
        // First, get the list of reports
        const result = await getParsedCreditReports({ clientUserId });
        
        if (!result.reports || result.reports.length === 0) {
          setReportError('No parsed credit reports found. Please upload a credit report first.');
          return;
        }
        
        // Fetch full report data for each report
        const fullReports = await Promise.all(
          result.reports.map(async (report) => {
            try {
              const fullReportResponse = await getParsedCreditReportById(report.id);
              // getParsedCreditReportById returns { success: true, report: { ...reportData } }
              // The report object contains reportData as a nested property
              const fullReport = fullReportResponse.report || fullReportResponse;
              return {
                ...report,
                reportData: fullReport.reportData || fullReport
              };
            } catch (err) {
              console.error(`Error loading full report ${report.id}:`, err);
              // Return the minimal report if full fetch fails
              return report;
            }
          })
        );
        
        setReportData(fullReports);
        
        // Aggregate summary from all reports
        const aggregatedSummary = aggregateReportSummaries(fullReports);
        setReportSummary(aggregatedSummary);
        
        // Track form open
        trackFeatureUsed({
          section: 'actionable_plans',
          feature: 'report_based_assessment',
          action: 'open',
          source: 'modal',
          additionalParams: {
            reports_count: result.reports.length
          }
        });
      } catch (err) {
        console.error('Error loading credit reports:', err);
        setReportError(err.message || 'Failed to load credit report data.');
      } finally {
        setLoadingReports(false);
      }
    };
    
    loadReports();
  }, [clientUserId, aggregateReportSummaries]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Credit score is required
    if (!formData.creditScore.trim()) {
      newErrors.creditScore = t('reportBasedForm.creditScoreRequired', 'Please enter your current credit score.');
    } else {
      const score = parseInt(formData.creditScore, 10);
      if (isNaN(score) || score < 300 || score > 850) {
        newErrors.creditScore = t('reportBasedForm.creditScoreInvalid', 'Credit score must be between 300 and 850.');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'report_based_assessment',
        action: 'validation_error',
        source: 'form'
      });
      return;
    }

    if (!currentUser) {
      setErrors({ submit: t('reportBasedForm.loginRequired', 'Please log in to generate a credit improvement plan.') });
      return;
    }

    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
      return;
    }

    await submitPlan();
  };

  const submitPlan = async () => {
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'report_based_assessment',
      action: 'submit',
      source: 'form',
      additionalParams: {
        timeline: formData.timeline,
        priority: formData.improvementPriority,
        has_income: !!formData.monthlyIncome
      }
    });

    setIsSubmitting(true);
    setGenerationProgress(t('reportBasedForm.generatingPlan', 'Generating your personalized credit improvement plan...'));
    
    try {
      const goalId = `improve_personal_credit_${Date.now()}`;
      
      // Merge report data with form input
      const assessmentData = {
        formId: 'report_based_credit_improvement',
        subcategory: 'personal_credit_improvement',
        goalId: goalId,
        goal: {
          id: goalId,
          goal: t('reportBasedForm.goalTitle', 'Improve Overall Personal Credit'),
          timeline: formData.timeline
        },
        formData: {
          // From parsed credit reports
          credit_utilization: reportSummary?.creditUtilization,
          total_accounts: reportSummary?.totalAccounts,
          open_accounts: reportSummary?.openAccounts,
          closed_accounts: reportSummary?.closedAccounts,
          delinquent_accounts: reportSummary?.delinquentAccounts,
          derogatory_accounts: reportSummary?.derogatoryAccounts,
          hard_inquiries: reportSummary?.hardInquiries,
          collections_count: reportSummary?.collections,
          total_balances: reportSummary?.totalBalances,
          revolving_balance: reportSummary?.revolvingBalance,
          total_credit_limit: reportSummary?.totalCreditLimit,
          bureaus_analyzed: reportSummary?.bureausAnalyzed?.join(', '),
          oldest_account_date: reportSummary?.oldestAccountDate || 'Unknown',
          // Credit cards list with opening dates for LLM context
          credit_cards_summary: reportSummary?.creditCards?.map(card =>
            `${card.bankName} (opened: ${card.dateOpened || 'Unknown'}${card.creditLimit ? `, limit: $${card.creditLimit.toLocaleString()}` : ''})`
          ).join('; ') || 'No credit cards found',
          // From user input
          current_credit_score: formData.creditScore,
          monthly_income: formData.monthlyIncome || 'Not specified',
          improvement_priority: formData.improvementPriority,
          timeline: formData.timeline,
          // Context summary for AI
          credit_situation_summary: buildCreditSituationSummary()
        },
        submittedAt: new Date().toISOString(),
        user_id: currentUser?.uid || 'anonymous',
        form_type: 'report_based_credit_improvement',
        // Scope data for mentor mode
        ...(isMentorMasquerade && effectiveUserId && actualUserId ? {
          scopeData: {
            clientUserId: effectiveUserId,
            mentorId: actualUserId,
            isMentorCreated: true
          }
        } : {})
      };

      console.log('Submitting report-based assessment:', assessmentData);
      
      // Submit for AI analysis
      const result = await submitWithRateLimit(assessmentData);
      console.log('AI Analysis received:', result);
      setAiAnalysis(result.data.aiAnalysis);
      
      // Generate complete credit plan
      setGenerationProgress(t('assessmentForm.phase1', 'Phase 1: Generating credit plan structure...'));
      const planResult = await generateCompleteCreditPlan(assessmentData, result.data.aiAnalysis, setGenerationProgress);
      console.log('Credit plan generated:', planResult);
      
      setCreditPlan(planResult);
      setGenerationProgress(t('assessmentForm.planGenerated', 'Plan generated successfully!'));
      setShowResults(true);
      
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'report_based_assessment',
        action: 'plan_generated',
        source: 'form',
        additionalParams: {
          timeline: formData.timeline,
          priority: formData.improvementPriority,
          plan_steps: planResult?.planStructure ? Object.keys(planResult.planStructure).length - 1 : 0
        }
      });
      
      if (onSubmit) {
        onSubmit({
          ...assessmentData,
          creditPlan: planResult
        });
      }
    } catch (error) {
      console.error('Report-based assessment submission error:', error);
      
      if (error.rateLimited) {
        const seconds = Math.ceil(error.retryAfter / 1000);
        setErrorModal({
          show: true,
          type: 'warning',
          title: t('reportBasedForm.rateLimitTitle', 'Rate Limit Exceeded'),
          message: t('reportBasedForm.rateLimitExceeded', `Too many requests. Please wait ${seconds} seconds and try again.`).replace('{seconds}', seconds.toString())
        });
      } else {
        setErrorModal({
          show: true,
          type: 'error',
          title: t('reportBasedForm.errorTitle', 'Submission Error'),
          message: t('reportBasedForm.errorSubmitting', `Error generating plan: ${error.message}`).replace('{error}', error.message)
        });
      }
      
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'report_based_assessment',
        action: error.rateLimited ? 'rate_limited' : 'error',
        source: 'form',
        additionalParams: {
          remaining_calls: getRemainingCalls(),
          error: error.message
        }
      });
    } finally {
      setIsSubmitting(false);
      setGenerationProgress('');
    }
  };

  const buildCreditSituationSummary = () => {
    const parts = [];

    if (formData.creditScore) {
      parts.push(`Current Credit Score: ${formData.creditScore}`);
    }

    if (reportSummary) {
      parts.push(`Total Accounts: ${reportSummary.totalAccounts} (${reportSummary.openAccounts} open, ${reportSummary.closedAccounts} closed)`);

      if (reportSummary.oldestAccountDate) {
        parts.push(`Oldest Account: ${reportSummary.oldestAccountDate}`);
      }

      if (reportSummary.creditUtilization != null) {
        parts.push(`Credit Utilization: ${reportSummary.creditUtilization}%`);
      }

      if (reportSummary.delinquentAccounts > 0) {
        parts.push(`Delinquent Accounts: ${reportSummary.delinquentAccounts}`);
      }

      if (reportSummary.derogatoryAccounts > 0) {
        parts.push(`Derogatory Accounts: ${reportSummary.derogatoryAccounts}`);
      }

      if (reportSummary.hardInquiries > 0) {
        parts.push(`Hard Inquiries: ${reportSummary.hardInquiries}`);
      }

      if (reportSummary.collections > 0) {
        parts.push(`Collections: ${reportSummary.collections}`);
      }

      // Add credit cards with opening dates
      if (reportSummary.creditCards && reportSummary.creditCards.length > 0) {
        const cardsList = reportSummary.creditCards.map(card =>
          `${card.bankName} (opened: ${card.dateOpened || 'Unknown'})`
        ).join(', ');
        parts.push(`Credit Cards: ${cardsList}`);
      }

      parts.push(`Bureaus Analyzed: ${reportSummary.bureausAnalyzed?.join(', ') || 'Unknown'}`);
    }

    if (formData.monthlyIncome) {
      parts.push(`Monthly Income: ${formData.monthlyIncome}`);
    }

    parts.push(`Improvement Priority: ${formData.improvementPriority}`);
    parts.push(`Target Timeline: ${formData.timeline}`);

    return parts.join('. ');
  };

  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    submitPlan();
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'report_based_assessment',
      action: 'disclaimer_rejected',
      source: 'modal'
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '--';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getUtilizationColor = (utilization) => {
    if (utilization == null) return 'neutral';
    if (utilization <= 10) return 'excellent';
    if (utilization <= 30) return 'good';
    if (utilization <= 50) return 'fair';
    if (utilization <= 75) return 'poor';
    return 'bad';
  };

  // Loading state
  if (loadingReports) {
    return (
      <div className="report-based-form-overlay">
        <div className="report-based-form-modal">
          <div className="report-based-form-header">
            <h2>{t('reportBasedForm.title', 'Improve Personal Credit')}</h2>
            <button 
              type="button" 
              className="close-button"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div className="report-based-form-body">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('reportBasedForm.loadingReports', 'Loading your credit report data...')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - no reports found
  if (reportError) {
    return (
      <div className="report-based-form-overlay">
        <div className="report-based-form-modal">
          <div className="report-based-form-header">
            <h2>{t('reportBasedForm.title', 'Improve Personal Credit')}</h2>
            <button 
              type="button" 
              className="close-button"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div className="report-based-form-body">
            <div className="error-container">
              <span className="error-icon">⚠️</span>
              <h3>{t('reportBasedForm.errorLoadingTitle', 'Unable to Load Reports')}</h3>
              <p>{reportError}</p>
              <button 
                type="button"
                className="btn-secondary"
                onClick={onClose}
              >
                {t('reportBasedForm.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-based-form-overlay">
      <div className="report-based-form-modal">
        <div className="report-based-form-header">
          <h2>{t('reportBasedForm.title', 'Improve Personal Credit')}</h2>
          <button 
            type="button" 
            className="close-button"
            onClick={() => {
              trackFeatureUsed({
                section: 'actionable_plans',
                feature: 'report_based_assessment',
                action: 'close',
                source: 'close_button'
              });
              onClose();
            }}
            disabled={isSubmitting || showResults}
          >
            ×
          </button>
        </div>

        <div className="report-based-form-body">
          {(!showResults && !aiAnalysis) && (
            <>
              {/* Credit Report Summary Section */}
              {reportSummary && (
                <div className="credit-report-summary">
                  <h3>
                    <span className="section-icon">📊</span>
                    {t('reportBasedForm.yourCreditSnapshot', 'Your Credit Snapshot')}
                  </h3>
                  <p className="summary-subtitle">
                    {t('reportBasedForm.dataFromReports', 'Based on your uploaded credit reports from:')} {reportSummary.bureausAnalyzed?.map(b => b.charAt(0).toUpperCase() + b.slice(1)).join(', ')}
                  </p>
                  
                  <div className="summary-grid">
                    <div className="summary-card">
                      <span className="card-label">{t('reportBasedForm.totalAccounts', 'Total Accounts')}</span>
                      <span className="card-value">{reportSummary.totalAccounts}</span>
                      <span className="card-detail">{reportSummary.openAccounts} {t('reportBasedForm.open', 'open')} / {reportSummary.closedAccounts} {t('reportBasedForm.closed', 'closed')}</span>
                    </div>

                    <div className={`summary-card utilization ${getUtilizationColor(reportSummary.creditUtilization)}`}>
                      <span className="card-label">{t('reportBasedForm.creditUtilization', 'Credit Utilization')}</span>
                      <span className="card-value">
                        {reportSummary.creditUtilization != null ? `${reportSummary.creditUtilization}%` : '--'}
                      </span>
                      <span className="card-detail">
                        {formatCurrency(reportSummary.revolvingBalance)} / {formatCurrency(reportSummary.totalCreditLimit)}
                      </span>
                    </div>

                    <div className="summary-card">
                      <span className="card-label">{t('reportBasedForm.oldestAccount', 'Oldest Account')}</span>
                      <span className="card-value">{reportSummary.oldestAccountDate || '--'}</span>
                    </div>

                    <div className={`summary-card ${reportSummary.hardInquiries > 5 ? 'warning' : ''}`}>
                      <span className="card-label">{t('reportBasedForm.hardInquiries', 'Hard Inquiries')}</span>
                      <span className="card-value">{reportSummary.hardInquiries}</span>
                    </div>

                    <div className={`summary-card ${reportSummary.delinquentAccounts > 0 ? 'warning' : ''}`}>
                      <span className="card-label">{t('reportBasedForm.delinquentAccounts', 'Delinquent')}</span>
                      <span className="card-value">{reportSummary.delinquentAccounts}</span>
                    </div>

                    <div className={`summary-card ${reportSummary.derogatoryAccounts > 0 ? 'danger' : ''}`}>
                      <span className="card-label">{t('reportBasedForm.derogatoryAccounts', 'Derogatory')}</span>
                      <span className="card-value">{reportSummary.derogatoryAccounts}</span>
                    </div>

                    <div className={`summary-card ${reportSummary.collections > 0 ? 'danger' : ''}`}>
                      <span className="card-label">{t('reportBasedForm.collections', 'Collections')}</span>
                      <span className="card-value">{reportSummary.collections}</span>
                    </div>

                    <div className="summary-card">
                      <span className="card-label">{t('reportBasedForm.creditCards', 'Credit Cards')}</span>
                      <span className="card-value">{reportSummary.creditCards?.length || 0}</span>
                    </div>
                  </div>

                  {/* Credit Cards List */}
                  {reportSummary.creditCards && reportSummary.creditCards.length > 0 && (
                    <div className="credit-cards-list">
                      <h4>{t('reportBasedForm.yourCreditCards', 'Your Credit Cards')}</h4>
                      <div className="cards-chips">
                        {reportSummary.creditCards.map((card, idx) => (
                          <div key={idx} className="card-chip">
                            <span className="chip-name">{card.bankName}</span>
                            {card.dateOpened && (
                              <span className="chip-date">{t('reportBasedForm.opened', 'Opened')}: {card.dateOpened}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Minimal Form for Missing Info */}
              <form onSubmit={handleSubmit} className="report-based-form">
                <div className="form-section">
                  <h3>
                    <span className="section-icon">📝</span>
                    {t('reportBasedForm.additionalInfo', 'Additional Information Needed')}
                  </h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="creditScore">
                        {t('reportBasedForm.currentCreditScore', 'Current Credit Score')} <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="creditScore"
                        value={formData.creditScore}
                        onChange={(e) => handleInputChange('creditScore', e.target.value)}
                        placeholder={t('reportBasedForm.creditScorePlaceholder', 'e.g., 720')}
                        min="300"
                        max="850"
                        disabled={isSubmitting}
                      />
                      {errors.creditScore && (
                        <span className="error-message">{errors.creditScore}</span>
                      )}
                      <span className="field-hint">{t('reportBasedForm.creditScoreHint', 'Your current FICO or VantageScore (300-850)')}</span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="monthlyIncome">
                        {t('reportBasedForm.monthlyIncome', 'Monthly Income')} <span className="optional">({t('reportBasedForm.optional', 'optional')})</span>
                      </label>
                      <input
                        type="text"
                        id="monthlyIncome"
                        value={formData.monthlyIncome}
                        onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                        placeholder={t('reportBasedForm.incomePlaceholder', 'e.g., $5,000')}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="improvementPriority">
                        {t('reportBasedForm.improvementPriority', 'Improvement Priority')}
                      </label>
                      <select
                        id="improvementPriority"
                        value={formData.improvementPriority}
                        onChange={(e) => handleInputChange('improvementPriority', e.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value="overall">{t('reportBasedForm.priorityOverall', 'Overall Credit Score')}</option>
                        <option value="utilization">{t('reportBasedForm.priorityUtilization', 'Lower Credit Utilization')}</option>
                        <option value="payment_history">{t('reportBasedForm.priorityPaymentHistory', 'Improve Payment History')}</option>
                        <option value="remove_negatives">{t('reportBasedForm.priorityRemoveNegatives', 'Remove Negative Items')}</option>
                        <option value="build_credit">{t('reportBasedForm.priorityBuildCredit', 'Build Credit History')}</option>
                        <option value="reduce_inquiries">{t('reportBasedForm.priorityReduceInquiries', 'Reduce Hard Inquiries')}</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="timeline">{t('reportBasedForm.timeline', 'Target Timeline')}</label>
                      <select
                        id="timeline"
                        value={formData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value="1-3 months">1-3 {t('reportBasedForm.months', 'months')}</option>
                        <option value="3-6 months">3-6 {t('reportBasedForm.months', 'months')}</option>
                        <option value="6-12 months">6-12 {t('reportBasedForm.months', 'months')}</option>
                        <option value="12-24 months">12-24 {t('reportBasedForm.months', 'months')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      trackFeatureUsed({
                        section: 'actionable_plans',
                        feature: 'report_based_assessment',
                        action: 'cancel',
                        source: 'button'
                      });
                      onClose();
                    }}
                    className="cancel-button"
                    disabled={isSubmitting}
                  >
                    {t('reportBasedForm.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting 
                      ? t('reportBasedForm.generating', 'Generating Plan...')
                      : t('reportBasedForm.generatePlan', 'Generate Improvement Plan')}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Progress Indicator */}
          {isSubmitting && generationProgress && (
            <div className="generation-progress">
              <div className="progress-content">
                <h3>{t('assessmentForm.generatingCreditPlan', 'Generating Your Credit Plan')}</h3>
                <p>{t('reportBasedForm.generatingMessage', 'Analyzing your credit report data and creating personalized recommendations...')}</p>
                <p>{generationProgress}</p>
                <div className="progress-spinner"></div>
              </div>
            </div>
          )}

          {/* AI Analysis Results */}
          {showResults && aiAnalysis && (
            <div className="ai-analysis-results">
              <div className="results-header">
                <h3>{t('assessmentForm.aiAnalysisRecommendations', 'AI Analysis & Recommendations')}</h3>
                <button onClick={() => setShowResults(false)} className="close-results">
                  ×
                </button>
              </div>
              <div className="analysis-content">
                <div className="analysis-text">
                  <MarkdownRenderer 
                    content={aiAnalysis}
                    className="analysis-markdown"
                    showFullText={showFullAnalysis}
                    onToggleText={() => setShowFullAnalysis(!showFullAnalysis)}
                    showToggleButton={true}
                    showMoreText={t('reportBasedForm.showMore', 'Show More')}
                    showLessText={t('reportBasedForm.showLess', 'Show Less')}
                  />
                </div>
              </div>
              
              {/* Credit Plan Results */}
              {creditPlan && (
                <div className="credit-plan-results">
                  <div className="plan-header">
                    <h3>{t('assessmentForm.personalizedCreditPlan', 'Your Personalized Credit Improvement Plan')}</h3>
                    <p>{t('assessmentForm.generatedSteps', 'Generated {count} actionable steps').replace('{count}', (creditPlan.planStructure ? Object.keys(creditPlan.planStructure).length - 1 : 0).toString())}</p>
                  </div>
                  <div className="plan-summary">
                    <div className="plan-stats">
                      <div className="stat">
                        <span className="stat-label-form">{t('assessmentForm.goalLabel', 'Goal')}</span>
                        <span className="stat-value">{creditPlan.goal.goal}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label-form">{t('assessmentForm.timelineLabel', 'Timeline')}</span>
                        <span className="stat-value">{creditPlan.goal.timeline}</span>
                      </div>
                    </div>
                  </div>
                  <div className="plan-actions">
                    <button 
                      onClick={() => {
                        trackCTAClick({
                          section: 'actionable_plans',
                          action: 'view_complete_plan',
                          source: 'button',
                          additionalParams: {
                            form_type: 'report_based'
                          }
                        });
                        
                        window.location.href = '/history';
                      }} 
                      className="btn-primary"
                    >
                      {t('assessmentForm.viewCompletePlan', 'View Complete Plan')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
      />
      
      {/* Error Modal */}
      <Modal 
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, message: '' })}
        type={errorModal.type || 'error'}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default ReportBasedAssessmentForm;

